/**
 * Delay time resolution, shared by the echo worklet and the fallback graph.
 *
 * Kept separate from the audio graph so the arithmetic can be tested without a
 * WebAudio context, and so the worklet and the fallback can never disagree
 * about where a repeat lands.
 */

export const NOTE_DIVISIONS = {
  '1/2': 2, '1/2T': 4 / 3, '1/2D': 3,
  '1/4': 1, '1/4T': 2 / 3, '1/4D': 1.5,
  '1/8': 0.5, '1/8T': 1 / 3, '1/8D': 0.75,
  '1/16': 0.25, '1/16T': 1 / 6, '1/16D': 0.375
};

export const ECHO_STYLES = ['digital', 'tape', 'analog', 'lofi', 'telephone', 'fuzz'];

// Sessions saved before the echo engine existed store 'clean'.
const LEGACY_STYLES = { clean: 'digital' };

/** Resolve a stored character name onto a style the engine knows. */
export function resolveEchoStyle(character) {
  const name = LEGACY_STYLES[character] || character;
  return ECHO_STYLES.includes(name) ? name : 'tape';
}

export const MAX_DELAY_MS = 4000;
const MIN_DELAY_MS = 1;

const finite = (value, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

/**
 * Resolve the left and right delay times in milliseconds.
 *
 * Only Dual mode offsets the right channel; Single and Ping-Pong both run one
 * time, because in Ping-Pong the stereo movement comes from the feedback
 * crossing channels rather than from the two sides differing.
 */
export function resolveDelayTimes(params = {}, bpm = 120) {
  const tempo = finite(bpm, 120);
  const useSync = Boolean(params.syncToTempo) && tempo > 0;

  let milliseconds = useSync
    ? (NOTE_DIVISIONS[params.noteValue] ?? 1) / tempo * 60000
    : finite(params.time, 375);

  const groove = finite(params.groove, 0);
  if (groove !== 0 && tempo > 0) {
    // Groove nudges the repeat off the grid by a fraction of a sixteenth.
    milliseconds += groove * 0.25 * (60000 / tempo) * 0.25;
  }

  const left = Math.min(MAX_DELAY_MS, Math.max(MIN_DELAY_MS, milliseconds));
  const spread = Math.min(1, Math.max(0, finite(params.spread, 0)));
  const right = params.mode === 'dual'
    ? Math.min(MAX_DELAY_MS, Math.max(MIN_DELAY_MS, left * (1 + spread * 0.5)))
    : left;

  return { timeLeftMs: left, timeRightMs: right };
}

/** Map the stored tone control (0..1) onto the echo's high-cut corner in Hz. */
export function resolveHighCutHz(tone) {
  const normalized = Math.min(1, Math.max(0, finite(tone, 0.72)));
  return 600 * Math.pow(20000 / 600, normalized);
}

/** Map the stored HPF control (0..1) onto the echo's low-cut corner in Hz. */
export function resolveLowCutHz(hpf) {
  const normalized = Math.min(1, Math.max(0, finite(hpf, 0.12)));
  return 20 * Math.pow(25, normalized);
}

/** Everything the echo worklet needs, derived from stored slot params. */
export function buildEchoParams(params = {}, bpm = 120) {
  const { timeLeftMs, timeRightMs } = resolveDelayTimes(params, bpm);
  return {
    mix: params.mix,
    timeLeftMs,
    timeRightMs,
    feedback: params.feedback,
    style: resolveEchoStyle(params.character),
    mode: params.mode,
    character: params.characterAmount ?? 0.6,
    wowFlutter: params.wowFlutter ?? 0.4,
    lowCut: resolveLowCutHz(params.hpf),
    highCut: resolveHighCutHz(params.tone),
    width: params.width ?? 1,
    saturation: params.saturation,
    ducking: params.ducking ?? 0,
    diffusion: params.diffusion ?? 0,
    crossfeed: params.crossfeed ?? 0,
    pitchSemitones: params.pitchSemitones ?? 0
  };
}
